import React, { useState, useEffect, useRef } from 'react';
import { Mail, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';

const EmailVerificationBanner = ({ user, onVerificationComplete }) => {
  const { updateProfile } = useAuth();
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);

  // Only log when emailVerified status actually changes, not on every user object change
  const prevEmailVerifiedRef = useRef(user?.emailVerified);
  useEffect(() => {
    const currentEmailVerified = user?.emailVerified;
    if (prevEmailVerifiedRef.current !== currentEmailVerified) {
      prevEmailVerifiedRef.current = currentEmailVerified;
      console.log('EmailVerificationBanner: Email verification status changed:', {
        email: user?.email,
        emailVerified: currentEmailVerified,
        userId: user?.id
      });
    }
  }, [user?.emailVerified, user?.email, user?.id]);

  // Don't show if user is already verified
  if (user?.emailVerified === true) {
    console.log('EmailVerificationBanner: User is verified, hiding banner');
    return null;
  }

  // Don't show for additional users - they were invited via email link, so email is verified
  // (even if emailVerified flag isn't set, the invitation acceptance proves email ownership)
  if (user?.role === 'additional_user') {
    console.log('EmailVerificationBanner: Additional user - hiding banner (invited via email)');
    return null;
  }

  const handleCheckVerificationStatus = async () => {
    setIsCheckingStatus(true);
    setResendError(null);
    try {
      // Fetch fresh user profile to check email verification status
      const response = await api.get('/users/profile');
      console.log('EmailVerificationBanner: Fresh profile data:', response.data);
      
      if (response.data.success && response.data.user.emailVerified === true) {
        console.log('EmailVerificationBanner: Email is verified, updating user profile');
        // Email is verified, update the cached user data
        await updateProfile({ emailVerified: true });
        console.log('EmailVerificationBanner: Profile updated, banner should disappear');
        
        if (onVerificationComplete) {
          onVerificationComplete();
        }
        
        // Force a page refresh to ensure banner disappears
        window.location.reload();
      } else {
        console.log('EmailVerificationBanner: Email not verified yet, status:', response.data.user.emailVerified);
        setResendError('Email not verified yet. Please check your inbox and spam folder.');
      }
    } catch (error) {
      console.error('Check verification status error:', error);
      setResendError('Unable to check status. Please try again.');
    } finally {
      setIsCheckingStatus(false);
    }
  };

  const handleResendVerification = async () => {
    setIsResending(true);
    setResendError(null);
    setResendSuccess(false);

    try {
      // Use API with auth token for resend verification
      const response = await api.post('/api/users/resend-verification', { 
        email: user?.email 
      });
      
      console.log('Resend verification response:', response.data);
      
      if (response.data.success) {
        setResendSuccess(true);
        setTimeout(() => setResendSuccess(false), 5000);
      } else {
        setResendError(response.data.error || 'Failed to resend verification email');
      }
    } catch (error) {
      console.error('Resend verification error:', error);
      setResendError(error.response?.data?.error || error.message || 'Failed to resend verification email');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <AlertCircle className="h-5 w-5 text-yellow-400" />
        </div>
        <div className="ml-3 flex-1">
          <div>
            <h3 className="text-sm font-medium text-yellow-800">
              Email verification required
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>
                You must verify your email address <strong>{user?.email}</strong> before you can send invoices. 
                Please check your email (including junk/spam folder) and click the verification link to activate all features.
              </p>
              <p className="mt-2 font-medium">
                📧 If the email is in spam, please mark it as "Not Spam" to ensure you receive future important notifications.
              </p>
            </div>
          </div>
          
          <div className="mt-4 flex items-center space-x-4">
            <button
              onClick={handleResendVerification}
              disabled={isResending}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-yellow-700 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isResending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-700 mr-2"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Resend Email
                </>
              )}
            </button>
            
            <button
              onClick={handleCheckVerificationStatus}
              disabled={isCheckingStatus}
              className="inline-flex items-center text-sm font-medium text-yellow-700 hover:text-yellow-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCheckingStatus ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-700 mr-2"></div>
                  Checking...
                </>
              ) : (
                'Check verification status'
              )}
            </button>
          </div>

          {resendSuccess && (
            <div className="mt-3 flex items-center text-sm text-green-700">
              <CheckCircle className="h-4 w-4 mr-2" />
              Verification email sent successfully!
            </div>
          )}

          {resendError && (
            <div className="mt-3 flex items-center text-sm text-red-700">
              <AlertCircle className="h-4 w-4 mr-2" />
              {resendError}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationBanner;
